import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  NavigationPanel,
  type NavigationPanelCreateAction,
  type NavigationPanelItem,
} from '@/shared/ui/layout';
import { projectFoldersQuery, type ProjectFolder } from '@/entities/project-folder';
import { toggleExpandedFolder, useExpandedFolderIds } from '../../model';
import { ApprovalProgressBadge } from '../ApprovalProgressBadge';

export const RAIL_PAGE_SIZE = 100;

interface ApprovalFolderRailProps {
  projectId: string;
  /** Folder currently open in the content pane; drives the selected row. */
  selectedFolderId?: string;
  onSelectFolder: (folder: ProjectFolder) => void;
  /** Omit where the caller cannot create folders; the add control then hides. */
  onCreateFolder?: () => void;
}

/**
 * The approval section's folder tree.
 *
 * Rows nest one level per depth and each carries its own approval progress, so
 * the whole section's state reads without opening anything. Children are
 * fetched per branch on first expand rather than up front — a project can carry
 * a deep tree and most of it is never opened.
 */
export function ApprovalFolderRail(props: ApprovalFolderRailProps) {
  const { t } = useTranslation('projects');
  const expandedIds = useExpandedFolderIds(props.projectId);

  const root = useQuery(
    projectFoldersQuery(props.projectId, { page: 1, pageSize: RAIL_PAGE_SIZE }),
  );

  // One query per opened branch. Closing one drops its query but not its cached
  // data, so reopening is instant.
  const branches = useQueries({
    queries: expandedIds.map((folderId) =>
      projectFoldersQuery(props.projectId, {
        parentId: folderId,
        page: 1,
        pageSize: RAIL_PAGE_SIZE,
      }),
    ),
  });
  const branchByFolderId = new Map(
    expandedIds.map((folderId, index) => [folderId, branches[index]]),
  );

  // One page deep. A project past that would lose the folders below the cut
  // with nothing to say it had, so the count that did not fit is stated rather
  // than dropped.
  const folders = root.data?.items ?? [];
  const unlistedCount = (root.data?.total ?? 0) - folders.length;

  // The panel reports a pick by id, and this is the only place holding the
  // folder behind each row.
  const foldersById = new Map<string, ProjectFolder>();
  for (const folder of folders) foldersById.set(folder.id, folder);
  for (const branch of branches) {
    for (const child of branch.data?.items ?? []) foldersById.set(child.id, child);
  }

  const toItem = (folder: ProjectFolder): NavigationPanelItem => {
    const hasChildren = folder.foldersCount > 0;
    const isExpanded = expandedIds.includes(folder.id);
    const branch = branchByFolderId.get(folder.id);

    return {
      id: folder.id,
      label: folder.name,
      // Only where the assets counted are the folder's own. The progress rolls
      // up the whole subtree, so a folder of folders would report assets it
      // does not hold, on a row whose children each report the same ones again.
      trailing: hasChildren ? undefined : (
        <ApprovalProgressBadge progress={folder.approvalProgress} display="count" />
      ),
      branch: hasChildren
        ? {
            isExpanded,
            isLoading: branch?.isLoading,
            onToggle: () => toggleExpandedFolder(props.projectId, folder.id),
            toggleLabel: isExpanded
              ? t('projectDetail.approvalFolderRail.collapseFolder', { name: folder.name })
              : t('projectDetail.approvalFolderRail.expandFolder', { name: folder.name }),
          }
        : undefined,
      children: branch?.data?.items.map(toItem),
    };
  };

  const openFolder = (folderId: string) => {
    const folder = foldersById.get(folderId);
    if (!folder) return;
    props.onSelectFolder(folder);
    // Opening a folder of folders reveals what is under it, but never hides it:
    // collapsing is the chevron's job alone.
    if (folder.foldersCount > 0 && !expandedIds.includes(folderId)) {
      toggleExpandedFolder(props.projectId, folderId);
    }
  };

  const createAction: NavigationPanelCreateAction = props.onCreateFolder
    ? {
        createLabel: t('projectDetail.approvalFolderRail.newFolder'),
        canCreate: true,
        onCreate: props.onCreateFolder,
      }
    : {};

  return (
    <NavigationPanel
      {...createAction}
      data-testid="approval-folder-rail"
      title={t('projectDetail.approvalFolderRail.approvalFolders')}
      items={folders.map(toItem)}
      activeItemId={props.selectedFolderId ?? null}
      isLoading={root.isLoading}
      emptyMessage={t('projectDetail.approvalFolderRail.noFoldersYet')}
      // A tree that failed to load is not a project without folders, and the
      // two rendered identically: an empty rail beside a pane inviting you to
      // make your first folder, when the folders are there and unreachable.
      error={
        root.isError
          ? {
              title: t('projectDetail.approvalFolderRail.couldNotLoadFolders'),
              description: t('projectDetail.approvalFolderRail.foldersAreStillThere'),
              retryLabel: t('projectDetail.approvalFolderRail.tryAgain'),
              onRetry: () => void root.refetch(),
            }
          : undefined
      }
      onSelectItem={openFolder}
      footer={
        unlistedCount > 0 ? (
          <p className="px-sm text-body-sm text-muted-foreground">
            {t('projectDetail.approvalFolderRail.foldersNotShown', { count: unlistedCount })}
          </p>
        ) : undefined
      }
    />
  );
}
